import type { BadgeVariant } from "@design";
import { useTranslation } from "react-i18next";
import type { ContentFilterTrendPoint } from "@/api";
import { resources } from "@/i18n/i18n";
import { DEFAULT_RANGE, RANGE_DAYS, type RangeDays } from "../usage/usageRange";

/** The audited, read-only review of the chat a flagged event happened in (MHS-1070). */
export const reviewConversationPath = (eventUuid: string) => `/admin/content-safety/events/${eventUuid}/conversation`;

export const HIT_SOURCES = ["prompt", "completion"] as const;
export const SEVERITIES = ["high", "medium", "low", "safe"] as const;
type HitSource = (typeof HIT_SOURCES)[number];
type Severity = (typeof SEVERITIES)[number];
/** Azure `content_filter_results` keys with a translated name; any other category shows as stored. */
const CATEGORY_NAMES = resources.en.adminContentSafety.categories;
type Category = keyof typeof CATEGORY_NAMES;

const SEVERITY_VARIANT: Record<Severity, BadgeVariant> = {
  high: "danger",
  medium: "warning",
  low: "success",
  safe: "neutral",
};
export const severityVariant = (severity: string) => SEVERITY_VARIANT[severity as Severity] ?? "neutral";

/** The range and the events filters, as kept in the URL. */
export interface EventFilters {
  range: RangeDays;
  source?: HitSource;
  category?: string;
  severity?: Severity;
}

// Azure's category keys are lower snake_case, and the backend takes at most 50 characters; a link's other values are ignored.
const CATEGORY_KEY = /^[a-z0-9_]{1,50}$/;

export const oneOf = <T extends string>(values: readonly T[], value: string | null): T | undefined =>
  values.find((v) => v === value);

/** Filters from the URL; values the backend wouldn't accept are ignored. */
export function parseFilters(params: URLSearchParams): EventFilters {
  const category = params.get("category") ?? "";
  return {
    range: oneOf(RANGE_DAYS, params.get("range")) ?? DEFAULT_RANGE,
    source: oneOf(HIT_SOURCES, params.get("source")),
    category: CATEGORY_KEY.test(category) ? category : undefined,
    severity: oneOf(SEVERITIES, params.get("severity")),
  };
}

/** URL search params for `filters`, defaults left out. */
export function filtersToParams(filters: EventFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.range !== DEFAULT_RANGE) params.range = filters.range;
  if (filters.source) params.source = filters.source;
  if (filters.category) params.category = filters.category;
  if (filters.severity) params.severity = filters.severity;
  return params;
}

const utcDay = (date: Date) => date.toISOString().slice(0, 10);

/** One point per UTC day from `start` through `end`; the API only returns days with hits, the rest are zero. */
export function fillDays(start: string, end: Date, points: ContentFilterTrendPoint[]): ContentFilterTrendPoint[] {
  const byDay = new Map(points.map((point) => [point.day, point]));
  const last = utcDay(end);
  const days: ContentFilterTrendPoint[] = [];
  for (const cursor = new Date(start); utcDay(cursor) <= last; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const day = utcDay(cursor);
    days.push(byDay.get(day) ?? { day, hits: 0, blocked: 0 });
  }
  return days;
}

/** Display names for the stored source, category and severity values. */
export function useEventLabels() {
  const { t } = useTranslation("adminContentSafety");
  return {
    source: (source: string) => {
      const known = oneOf(HIT_SOURCES, source);
      return known ? t(`sources.${known}`) : source;
    },
    category: (category: string) =>
      Object.hasOwn(CATEGORY_NAMES, category) ? t(`categories.${category as Category}`) : category,
    severity: (severity: string | null) => {
      if (severity === null) return t("severities.none");
      const known = oneOf(SEVERITIES, severity);
      return known ? t(`severities.${known}`) : severity;
    },
  };
}
